import type { Lap } from "./laps";
import type { LapLabel } from "@contract/types";
import { matchKeyToLaps, type CorrectionKey } from "./correctionKey";

/** The ONE rule for "which lap does this label apply to?".
 *  Keyed label  → composite-key match; an unmatched key is an ORPHAN (null) and must
 *                 NEVER fall back to lapIndex (that would mis-apply it to a neighbour).
 *  Legacy label → no key fields at all → fall back to lapIndex.
 *  Returns an index into `laps`, or null.
 */
export function matchLabelToLapIdx(
  label: LapLabel,
  laps: Lap[],
  lapKeys: CorrectionKey[],
): number | null {
  if (
    label.offsetSeconds != null &&
    label.durationSeconds != null &&
    label.duplicateOrdinal != null
  ) {
    return matchKeyToLaps(
      {
        offsetSeconds: label.offsetSeconds,
        durationSeconds: label.durationSeconds,
        duplicateOrdinal: label.duplicateOrdinal,
      },
      lapKeys,
    );
  }
  const idx = laps.findIndex((l) => l.index === label.lapIndex);
  return idx === -1 ? null : idx;
}

/**
 * Overwrite each lap's EFFECTIVE strokeStyle with the user's persisted
 * strokeOverride, in place. Runs once in deriveWorkout right after buildLaps so
 * the corrected stroke flows uniformly into exclusions, aggregates, set grouping
 * and the matcher. watchStrokeStyle is preserved as the original watch value, so
 * the UI can still show "Apple Watch said: X" and the watch/you badge.
 *
 * A label only changes the stroke when it carries a non-null strokeOverride;
 * exclusion-only labels (kind "outlier"/"rest"/etc.) leave the stroke alone.
 *
 * Matching is delegated to matchLabelToLapIdx (T3b additive window) so this
 * function and the corrections view model can never disagree about which lap
 * a label applies to.
 */
export function applyStrokeOverrides(laps: Lap[], labels: LapLabel[]): void {
  if (labels.length === 0) return;

  const lapKeys = laps.map((l) => l.correctionKey);

  for (const label of labels) {
    if (!label.strokeOverride) continue;
    const targetIdx = matchLabelToLapIdx(label, laps, lapKeys);
    if (targetIdx === null) continue;
    laps[targetIdx].strokeStyle = label.strokeOverride;
  }
}
